import { useEffect, useState, useRef, useCallback, UIEvent } from 'react';
import { useSearchParams } from "react-router-dom";
import cn from 'classnames';

import * as interfaces from 'interfaces';
import { AnswerServiceInstance, createAnswerServiceInstance } from 'services';
import {
  useClientContext,
  useUserContext,
  useFilterAndSearchContext,
  useApplyFilterAndSearch,
  useTranslationContext
} from 'hooks';
import { getClientOptions } from 'scripts/clients/index'

import AnswerItem from '../AnswerThumbnail';
import SelectedAnswer from '../AnswerItem';
import Icons from 'shared/Icons';

import classes from './AnswersList.module.scss';

createAnswerServiceInstance();

export default function AnswersList(props: {
  isMobile?: boolean;
}) {
  const { isMobile } = props;
  const { client, answers, setAnswers } = useClientContext();
  const { user, userIsSet, isSignedIn } = useUserContext();
  const { filter, search } = useFilterAndSearchContext();
  const { t } = useTranslationContext();
  const {
    setOriginalItems: setOriginalAnswers,
    mappedItems: filteredAnswers,
    updateMappedItems: updateMappedAnswers
  } = useApplyFilterAndSearch<interfaces.AnswerInterface>({ filter, search });
  const [searchParams] = useSearchParams();
  const answerIdFromUrl = searchParams.get("answerId") || searchParams.get("aId") || searchParams.get("answervId");

  const [selectedAnswer, setSelectedAnswer]: [
    interfaces.AnswerInterface, (v: interfaces.AnswerInterface) => void
  ] = useState({} as interfaces.AnswerInterface);
  const [horizontalScrollPosition, setHorizontalScrollPosition] = useState(0);
  const [totalHorizontalScrollWidth, setTotalHorizontalScrollWidth] = useState(2);

  const listRef = useRef<HTMLHeadingElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleListScroll = useCallback(
    (direction: 'left' | 'right') => {
      if (direction === 'left') {
        listRef.current?.scroll({ left: listRef.current.scrollLeft - 186 });
      } else if (direction === 'right') {
        listRef.current?.scroll({ left: listRef.current.scrollLeft + 186 });
      }
    },
    [listRef]
  );

  const handleScroll = useCallback(
    (event: UIEvent<HTMLElement> | any) => {
      if (totalHorizontalScrollWidth === 2) {
        setTotalHorizontalScrollWidth(event?.target?.scrollWidth);
      }

      setHorizontalScrollPosition(event?.target?.scrollLeft);
    },
    [totalHorizontalScrollWidth]
  );

  const handleSelectedAnswerUpdate = useCallback(
    (answer: interfaces.AnswerInterface) => {
      updateMappedAnswers(((cur: interfaces.AnswerInterface[]) => {
        return cur.map(a => {
          if (a.id === answer.id) return { ...a, ...answer };

          return a;
        });
      }) as any);

      setSelectedAnswer(answer);
    },
    [updateMappedAnswers]
  );

  useEffect(() => {
    if (answerIdFromUrl){
      const answerFormUrl = answers.find((answer) => answer.id === answerIdFromUrl);
      if (answerFormUrl)
        setSelectedAnswer(answerFormUrl);
    }
  }, [answerIdFromUrl, answers]);


  useEffect(() => {
    const listContainer = listRef.current;

    if (listContainer)
      listContainer.addEventListener('scroll', handleScroll);

    return () => {
      if (listContainer)
        listContainer.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (!client.id || !user.id || !userIsSet) return;

    AnswerServiceInstance?.getAnswers(client.id, user.id).then((answers: interfaces.AnswerInterface[]) => {
      const preparedAnswers = answers
        .filter(a => !a.isDraft)
        .filter(a => (filter.length && filter.includes(a.question.category)) || !filter.length)
        .filter(
          a =>
            (search.length &&
              (a.question.text.toLowerCase().includes(search.toLowerCase()) ||
                a.question.text.toLowerCase() === search.toLowerCase())) ||
            !search.length
        );

      setOriginalAnswers(answers.filter(a => !a.isDraft));
      setAnswers(answers.filter(a => !a.isDraft));
      setSelectedAnswer(preparedAnswers?.length ? preparedAnswers[0] : ({} as interfaces.AnswerInterface));
    });
  }, [search, filter, userIsSet, isSignedIn]);

  function autoPlayNextVideo (answer: interfaces.AnswerInterface) {
    setSelectedAnswer(answer)

    // setVideoState(cur => ({
    //   ...cur,
    //   isFeedbackShown: false,
    //   ended: false,
    //   started: false,
    //   paused: false,
    //   seeked: true,
    //   seeking: true,
    //   isDonationBannerShown: false,
    //   isMetaInfoShown: true
    // }))

    setTimeout( () => {
      if (!videoRef?.current) return

      videoRef.current.currentTime = 0;
      videoRef.current.volume = 1;
      videoRef.current.play();
    }, 500 )
  }

  const clientOptions = getClientOptions(client)
  const customSubHeaderText = clientOptions?.customText?.videoThumbnailHeader || null;
  const customHeaderText = clientOptions?.customText?.answerListHeader || null;
  const clientName = (
    client.id == '211' ? 'The City'       : // BridgePort
    client.id == '209' ? 'City'           : // White Salmon
    client.id == '223' ? 'Bay City'       : // Bay City
    client.id == '235' ? 'Cold Lake'      : // Cold Lake
    client.id == '241' ? 'Fountain Hills' : // Fountain Hills
    `${client?.firstName || 'Candidate'}`
  )
  const headerText = customHeaderText || `${clientName}'s ${t("Answers")}`

  return (
    <>
      <h1 className={cn(classes.answersTitle, 'answersListTitle')} style={{ color: client?.openQuestionsAndAnswersColour }}>
        {headerText}
      </h1>

      <SelectedAnswer selectedAnswer={selectedAnswer} handleVideoUpdate={a => handleSelectedAnswerUpdate(a)} videoRef={videoRef} isMobile={isMobile} />

      {customSubHeaderText && <h2 className={cn(classes.subheader, 'videoListSubHeader')}>{customSubHeaderText}</h2>}

      <div
        className={cn(
          classes.AnswersList,
          'AnswersList',
          horizontalScrollPosition < (totalHorizontalScrollWidth / 4) * 3 && classes.rightArrowActive,
          horizontalScrollPosition > 0 && classes.leftArrowActive
        )}
      >
        <div className={cn(classes.arrow, 'arrow', classes.left)} onClick={() => handleListScroll('left')}>
          <Icons iconType="chevron-left" />
        </div>

        <div className={cn(classes.videoThumbnails, 'videoThumbnails')} ref={listRef}>
          {filteredAnswers.map((answer: interfaces.AnswerInterface) => (
            <AnswerItem key={answer.id} answer={answer} handleClick={autoPlayNextVideo} />
          ))}
        </div>

        <div className={cn(classes.arrow, 'arrow', classes.right)} onClick={() => handleListScroll('right')}>
          <Icons iconType="chevron-right" />
        </div>
      </div>
    </>
  );
}
